import { OutlineElement } from '../../base/outline-element/outline-element';
import { CSSResultGroup, html, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import componentStyles from './ghs-button-cta.css.lit';
import '../ghs-icon/ghs-icon';

/**
 * The Button CTA component
 *
 * @element ghs-button-cta
 */
@customElement('ghs-button-cta')
export class GhsButtonCta extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  /**
   * Whether or not this card has a rounded top/right corner.
   */
  @property({
    type: Boolean,
    attribute: 'is-top-right-rounded',
    reflect: true,
  })
  isRoundedTopRight = false;

  /**
   * Whether or not this card has a rounded top/left corner.
   */
  @property({
    type: Boolean,
    attribute: 'is-top-left-rounded',
    reflect: true,
  })
  isRoundedTopLeft = false;

  /**
   * Whether or not this card has a rounded bottom/right corner.
   */
  @property({
    type: Boolean,
    attribute: 'is-bottom-right-rounded',
    reflect: true,
  })
  isRoundedBottomRight = true;

  /**
   * Whether or not this card has a rounded bottom/left corner.
   */
  @property({
    type: Boolean,
    attribute: 'is-bottom-left-rounded',
    reflect: true,
  })
  isRoundedBottomLeft = true;

  /**
   * Disables the button function.
   */
  @property({
    type: Boolean,
    attribute: 'is-disabled',
    reflect: true,
  })
  isDisabled = false;

  /**
   * CTA url.
   */
  @property({ type: String, attribute: 'cta-url', reflect: true })
  ctaUrl: string;
  /**
   * CTA link caption.
   */
  @property({ type: String, attribute: 'cta-text', reflect: true })
  ctaText: string;

  @state() hasHeadingSlot = false;

  firstUpdated(): void {
    super.firstUpdated();
    this.hasHeadingSlot = this.querySelector('[slot="heading"]') !== null;
  }

  renderGHS(): TemplateResult {
    return this.isDisabled
      ? html` <div class="button-cta is-disabled">
          ${this.hasHeadingSlot
            ? html`
                <div class="button-cta-heading">
                  <slot name="heading"></slot>
                </div>
              `
            : ''}
          ${this.ctaText
            ? html`
                <div class="divider"></div>
                <div class="button-cta-text">
                  ${this.ctaText}
                  <ghs-icon
                    name="chevron-circle-white"
                    mobile-size="13px"
                    size="17px"
                    site=${this.site}
                  ></ghs-icon>
                </div>
              `
            : ''}
        </div>`
      : html`<a href=${this.ctaUrl} class="button-cta">
          ${this.hasHeadingSlot
            ? html`
                <div class="button-cta-heading">
                  <slot name="heading"></slot>
                </div>
              `
            : ''}
          ${this.ctaText
            ? html`
                <div class="divider"></div>
                <div class="button-cta-text">
                  ${this.ctaText}
                  <ghs-icon
                    name="chevron-circle-white"
                    mobile-size="13px"
                    size="17px"
                    site=${this.site}
                  ></ghs-icon>
                </div>
              `
            : ''}
        </a> `;
  }

  renderBH(): TemplateResult {
    return html`
      ${this.hasHeadingSlot
        ? html`
            <div class="button-cta-heading">
              <slot name="heading"></slot>
            </div>
          `
        : ''}
      ${this.ctaText
        ? html`
            <div class="divider"></div>
            <div class="button-cta-text">
              <ghs-button
                url=${this.ctaUrl}
                variant="cta-dark"
                disabled=${this.isDisabled}
                icon="chevron-right"
                icon-position="right"
              >
                ${this.ctaText}
              </ghs-button>
            </div>
          `
        : ''}
    `;
  }

  render(): TemplateResult {
    if (this.site === 'BH') {
      return this.renderBH();
    }
    return this.renderGHS();
  }
}
